/**
 * Navigation Logger Component
 * 
 * Logs internal route changes for debugging deep link and navigation issues.
 * Uses 200ms debounce to prevent duplicate logs when React Router triggers
 * multiple events (e.g., route change + query parameter update).
 * 
 * On slower devices, React render cycles can take ~80ms, so 200ms ensures
 * we reliably merge the route change and query param update into one log entry.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { addLog } from '../../utils/circularBuffer';

export function NavigationLogger() {
  const location = useLocation();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any pending log from previous navigation event
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce navigation logging by 200ms to capture final state
    // This merges "route change" + "query params" into one log instead of two
    debounceTimerRef.current = setTimeout(() => {
      addLog('info', `Navigation: ${location.pathname}`, {
        category: 'navigation',
        pathname: location.pathname,
        search: location.search || null,
        hash: location.hash || null,
        state: location.state || null
      });
    }, 200);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [location]);

  return null;
}
